import { PayloadAction } from '@reduxjs/toolkit';
import { quoteBuilderFieldValidators } from '@ui/features/quote-builder/state/fields';
import { WritableDraft } from 'immer';
import { z } from 'zod/v4';

import { QuoteBuilderStateType } from '../../types/state';
import { QuoteEditableStringFields } from '../../types/state/quote';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';

/**
 * Updates an editable string field in the QuoteBuilderState and performs validation if a validator is provided.
 *
 * Note that only the first validation error will be displayed in the UI if multiple errors occur.
 *
 * @param {WritableDraft<QuoteBuilderStateType>} state - The current state of the quote builder.
 * @param {PayloadAction<{field: QuoteEditableStringFields; value: string; validator?: z.ZodString;}>} action - The action containing the field to update, its new value, and an optional validator.
 */
export function editableStringFieldChanged(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<{
    field: QuoteEditableStringFields;
    value: string;
  }>,
) {
  const { field, value } = action.payload;
  const validator = quoteBuilderFieldValidators[field];

  let newValue: string | null = value;

  const invalidIssueId = buildIssueId({
    area: 'quote',
    field,
    type: 'invalid',
  });

  if (validator) {
    const parsedValue = validator.safeParse(value);

    if (!parsedValue.success) {
      const error = z.flattenError(parsedValue.error).formErrors[0];

      upsertIssue(state, invalidIssueId, {
        title: 'Invalid Field',
        description: error,
        severity: 'error',
        area: 'quote',
        type: 'invalid',
        action: {
          type: 'modify',
          elementId: buildElementId({
            uuid: state.uuid,
            field,
          }),
        },
      });

      return;
    }

    newValue = parsedValue.data;
  }

  deleteIssue(state, invalidIssueId);

  state[field] = newValue;
}
